/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import Navbar from "@/shared/Navbar";
import { useHeroSection } from "@/hooks/useHeroSection";
import { usePages } from "@/hooks/usePages";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

export default function FAQPage() {
  const { data: pages, loading } = usePages();
  const [faqData, setFaqData] = useState<any>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // 🟢 Fetch FAQ data
  useEffect(() => {
    const fetchFAQ = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/api/v1/faq`
        );
        const data = await res.json();
        if (data.success) setFaqData(data.data);
      } catch (err) {
        console.error("Failed to load FAQ data", err);
      }
    };
    fetchFAQ();
  }, []);

  // 🟣 Navbar dropdown
  const howToHubDropdown = useMemo(() => {
    const staticPages = [
      { label: "Buy Before You Sell", href: "/how-to-hub/buy-before-you-sell" },
    ];
    const dynamicPages =
      pages?.map((p: any) => ({
        label: p.title || "Untitled",
        href: `/how-to-hub/${p.slug}`,
      })) || [];
    return [
      ...(dynamicPages.length
        ? dynamicPages
        : loading
        ? [{ label: "Loading...", href: "#" }]
        : []),
      ...staticPages,
    ];
  }, [pages, loading]);

  // 🟡 Hero Section
  const HeroSection = useHeroSection({
    title: faqData?.hero?.title || "Frequently Asked Questions",
    subtitle:
      faqData?.hero?.description ||
      "Get quick answers and expert insights about how AskLocal works.",
    backgroundImage: faqData?.hero?.image || "/default-faq.jpg",
    buttonText: "Contact Support",
    buttonLink: "/contact",
  });

  // 🔽 Toggle FAQ + scroll
  const toggleFAQ = (index: number) => {
    const newIndex = openIndex === index ? null : index;
    setOpenIndex(newIndex);
    if (newIndex !== null) {
      setTimeout(() => {
        itemRefs.current[newIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
  };

  return (
    <div className="bg-linear-to-b from-gray-50 via-white to-gray-100">
      {/* 🧭 Navbar */}
      <Navbar
        defaultLogo="/logo/asklocal-white-logo.png"
        scrolledLogo="/logo/color-full-logo.png"
        isLight
        labelColor="#ffffff"
        labelHoverColor="#EB9C2C"
        buttonBg="#EB9C2C"
        buttonTextColor="#FFFFFF"
        buttonHoverBg="#4177BC"
        buttonHoverText="#ffffff"
        ctaText="Looking to Buy/Sell?"
        ctaLink="/buy-sell"
        navItems={[
          { label: "Home", href: "/" },
          { label: "How-To Hub", dropdown: howToHubDropdown },
          { label: "About Us", href: "/about-us" },
          { label: "Pro-finder", href: "/pro-finder" },
          { label: "FAQ", href: "/faq" },
        ]}
      />

      {/* 🖼 Hero Section */}
      <div>{HeroSection}</div>

      {/* 📚 FAQ Section */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-12 tracking-tight">
          {faqData?.hero?.title || "Your Questions, Answered"}
        </h2>

        {faqData?.faqs?.length ? (
          <div className="space-y-6">
            {faqData.faqs.map((item: any, index: number) => (
              <motion.div
                key={index}
                ref={(el: any) => (itemRefs.current[index] = el)}
                layout
                transition={{
                  layout: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                }}
                className="rounded-2xl bg-white/80 border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.05)] backdrop-blur-md hover:shadow-[0_4px_35px_rgba(0,0,0,0.07)] transition-all"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="flex justify-between items-center w-full px-8 py-6 text-left"
                >
                  <span className="text-lg md:text-xl font-semibold text-gray-800">
                    {item.question}
                  </span>

                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    className="shrink-0 text-[#4177BC]"
                  >
                    <ChevronDown className="w-6 h-6" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {openIndex === index && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0, paddingTop: 0 }}
                      animate={{ height: "auto", opacity: 1, paddingTop: 8 }}
                      exit={{ height: 0, opacity: 0, paddingTop: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 18,
                        mass: 0.8,
                      }}
                      className="px-8 pb-6 text-gray-700 text-base leading-relaxed border-t border-gray-100"
                    >
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                      >
                        {item.answer}
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">Loading FAQs...</p>
        )}
      </section>
    </div>
  );
}
